import type { UserMessageAttachment } from "@circle-react-shared/AiConversation/composer/Attachments/types";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import type { CircleAiDailyBrief } from "@circle-react/types/CircleAiDailyBrief";
import type { JSONContent } from "@tiptap/core";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { AiView } from "./intents/urlParams";

interface ActiveConversation {
  dailyBrief?: Pick<CircleAiDailyBrief, "day_number" | "brief_date"> | null;
  dailyBriefConversation?: boolean;
  id: string | null;
  projectId?: string | null;
  title: string | null;
}

export type CircleAiSurface = "panel" | "page";

// Page and panel CircleAiHome mounts both register reset handlers. Keep them
// per-surface so startNewChat/switchToNewThread can clear the live /ai page
// thread even when the (closed) panel was the last to register.
const switchToNewThreadHandlers: Record<CircleAiSurface, () => void> = {
  page: () => {},
  panel: () => {},
};

interface PendingComposerDraft {
  target: CircleAiSurface;
  text: string;
}

interface PendingInitialMessage {
  attachmentMeta?: UserMessageAttachment[];
  body: JSONContent;
  conversationId?: string;
  projectId?: string;
  signedIds?: string[];
  // Which CircleAiHome surface should consume this message. Prevents a
  // mounted-but-hidden panel from grabbing a message meant for the /ai page
  // (and vice versa) when both surfaces are transiently alive.
  target: CircleAiSurface;
  text: string;
}

// `panelState` lets a caller pick the width explicitly. Omitted, the actions
// keep their historical defaults: fullscreen for a new chat, docked for an
// opened conversation.
interface OpenPanelOptions {
  panelState?: AiView;
}

interface CircleAiStore {
  activeConversation: ActiveConversation;
  analyticsReportArtefactUuid: string | null;
  clearAnalyticsReport: () => void;
  closeAnalyticsReport: () => void;
  isDockRecentChatsOpen: boolean;
  isFullPageAiMode: boolean;
  isRecentChatsOpen: boolean;
  loadConversation: (id: string) => void;
  openAnalyticsReport: (artefactUuid: string) => void;
  openConversation: (
    id: string,
    title?: string | null,
    projectId?: string | null,
    options?: OpenPanelOptions,
  ) => void;
  pendingComposerDraft: PendingComposerDraft | null;
  pendingInitialMessage: PendingInitialMessage | null;
  setActiveConversation: (conversation: ActiveConversation) => void;
  setDockRecentChatsOpen: (open: boolean) => void;
  setFullPageAiMode: (v: boolean) => void;
  setIsRecentChatsOpen: (open: boolean) => void;
  setLoadConversation: (fn: (id: string) => void) => void;
  setPendingComposerDraft: (draft: PendingComposerDraft | null) => void;
  setPendingInitialMessage: (msg: PendingInitialMessage | null) => void;
  setSwitchToNewThread: (surface: CircleAiSurface, fn: () => void) => void;
  startChatWithPrompt: (
    prompt: string,
    options?: OpenPanelOptions & { openPanel?: boolean },
  ) => void;
  startNewChat: (options?: OpenPanelOptions & { openPanel?: boolean }) => void;
  switchToNewThread: () => void;
  toggleRecentChats: () => void;
}

export const useCircleAiStore = create<CircleAiStore>()(
  persist(
    (set, get) => ({
      activeConversation: { id: null, title: null, projectId: null },
      analyticsReportArtefactUuid: null,
      isDockRecentChatsOpen: false,
      isFullPageAiMode: false,
      isRecentChatsOpen: false,
      pendingComposerDraft: null,
      loadConversation: () => {},
      pendingInitialMessage: null,
      switchToNewThread: () => {
        switchToNewThreadHandlers.page();
        switchToNewThreadHandlers.panel();
      },
      openAnalyticsReport: artefactUuid => {
        set({ analyticsReportArtefactUuid: artefactUuid });
        useCircleAIShellStore.getState().setConversationPanelState("default");
      },
      closeAnalyticsReport: () => {
        set({ analyticsReportArtefactUuid: null });
        useCircleAIShellStore
          .getState()
          .setConversationPanelState("fullscreen");
      },
      clearAnalyticsReport: () => set({ analyticsReportArtefactUuid: null }),
      setActiveConversation: conversation =>
        set({ activeConversation: conversation }),
      setDockRecentChatsOpen: open => set({ isDockRecentChatsOpen: open }),
      setFullPageAiMode: v => set({ isFullPageAiMode: v }),
      setIsRecentChatsOpen: open => set({ isRecentChatsOpen: open }),
      setLoadConversation: fn => set({ loadConversation: fn }),
      setPendingComposerDraft: draft => set({ pendingComposerDraft: draft }),
      setPendingInitialMessage: msg => set({ pendingInitialMessage: msg }),
      setSwitchToNewThread: (surface, fn) => {
        switchToNewThreadHandlers[surface] = fn;
      },
      startChatWithPrompt: (prompt, options) => {
        set({
          pendingComposerDraft: {
            target: options?.openPanel === false ? "page" : "panel",
            text: prompt,
          },
        });
        get().startNewChat(options);
      },
      startNewChat: ({ openPanel = true, panelState } = {}) => {
        // Reset activeConversation up-front so CircleAiHome renders a blank thread
        // even when invoked before the panel has mounted (switchToNewThread is
        // a no-op until then).
        set({
          activeConversation: {
            id: null,
            title: null,
            projectId: null,
            dailyBrief: null,
          },
          analyticsReportArtefactUuid: null,
          isRecentChatsOpen: false,
        });
        get().switchToNewThread();
        // The full-page /ai dock starts a new chat without the conversation
        // panel (the page keeps it closed); skip forcing it open there.
        if (openPanel) {
          const shellStore = useCircleAIShellStore.getState();
          shellStore.setConversationPanelState(panelState ?? "fullscreen");
          // Without this the dock falls back to the community switcher and
          // loses Skills, Projects and recent chats.
          shellStore.setAdminDockForced(true);
        }
      },
      openConversation: (id, title = null, projectId = null, options = {}) => {
        // Seed activeConversation up-front so CircleAiHome loads it on mount
        // even when invoked before the panel has mounted (loadConversation is a
        // no-op until then); when the panel is already open it switches threads.
        set({
          activeConversation: { id, title, projectId, dailyBrief: null },
          analyticsReportArtefactUuid: null,
          isRecentChatsOpen: false,
          isDockRecentChatsOpen: false,
        });
        get().loadConversation(id);
        // Open the panel, but don't downgrade an already-fullscreen panel to
        // default — opening a conversation should keep the user's fullscreen view
        // (e.g. clicking a recent chat from the fullscreen new-chat empty state).
        // An explicit panelState overrides that.
        const shellStore = useCircleAIShellStore.getState();
        if (options.panelState) {
          shellStore.setConversationPanelState(options.panelState);
        } else if (shellStore.conversationPanelState !== "fullscreen") {
          shellStore.setConversationPanelState("default");
        }
        shellStore.setAdminDockForced(true);
      },
      toggleRecentChats: () => {
        const shellStore = useCircleAIShellStore.getState();
        if (shellStore.conversationPanelState === "closed") {
          // RecentChatsDrawer renders inside the panel, so opening the drawer
          // from the dock requires opening the panel too.
          shellStore.setConversationPanelState("default");
          set({ isRecentChatsOpen: true });
          return;
        }
        set({ isRecentChatsOpen: !get().isRecentChatsOpen });
      },
    }),
    {
      name: "circle-ai-home",
      version: 2,
      partialize: ({ activeConversation }) => ({
        activeConversation: { id: activeConversation.id, title: null },
      }),
    },
  ),
);
